import {ReactElement, useEffect, useMemo, useRef, useState} from "react";
import * as css from "./styles/pmsPropertyList.module.scss";
import {Field, MediaInput, Label} from '@zendeskgarden/react-forms';
import {Button} from "@zendeskgarden/react-buttons";
import {ReactComponent as SearchIcon} from '@zendeskgarden/svg-icons/src/16/search-stroke.svg';
import {ReactComponent as CheckIcon} from '@zendeskgarden/svg-icons/src/16/check-lg-fill.svg';
import {Cell, Head, HeaderRow, SortableCell, HeaderCell, Body as TBody, Row, Table} from "@zendeskgarden/react-tables";
import {nextSortType, SortType} from "../../../../utils/sorting";
import {ReactComponent as AlertIcon} from '@zendeskgarden/svg-icons/src/16/alert-warning-fill.svg';
import {Tooltip} from "@zendeskgarden/react-tooltips";
import "native-injects";
import {ManualProperty} from "../logic/types";

type PMSProperty = {
    name: string;
    seenEarliest: string;
    seenLatest: string;
};

type PMSPropertyWithUsage = PMSProperty & {
    isUsedAlready: boolean;
};

export type PMSPropertyListProps = {
    pmsPropertiesData: PMSProperty[] | undefined;
    existingProperties: ManualProperty[] | undefined;
    onPropertySelect: (propertyName: string) => void;
};

type SortColumn = "NAME" | "SEEN_EARLIEST" | "USED_ALREADY";
type SortField = keyof Pick<PMSPropertyWithUsage, "name" | "seenEarliest" | "isUsedAlready">;

export function PMSPropertyList(props: PMSPropertyListProps): ReactElement {
    const {pmsPropertiesData, existingProperties, onPropertySelect} = props;

    const [searchString, setSearchString] = useState<string>();
    const [sortColumn, setSortColumn] = useState<SortColumn>();
    const [sortType, setSortType] = useState<SortType>();
    const bodyRef = useRef(null);

    const [pmsPropertiesWithUsage, setPmsPropertiesWithUsage] = useState<PMSPropertyWithUsage[]>();
    const [latestDate, setLatestDate] = useState<string>();

    useEffect(() => {
        if(!pmsPropertiesData || !existingProperties) {
            setPmsPropertiesWithUsage([]);
            setLatestDate(undefined);
            return;
        }

        // Find the latest date
        const maxSeenLatest = pmsPropertiesData.maxValue(property => property.seenLatest);
        setLatestDate(maxSeenLatest);

        // Create a set of existing property system names for quick lookup
        const existingPropertyNames = new Set(existingProperties.map(p => p.systemName));

        // Process PMS properties and mark which ones are already used
        const propertiesWithUsage: PMSPropertyWithUsage[] = [];
        for(const pmsProperty of pmsPropertiesData) {
            if(pmsProperty.seenLatest === maxSeenLatest) {
                propertiesWithUsage.push({
                    name: pmsProperty.name,
                    seenEarliest: pmsProperty.seenEarliest,
                    seenLatest: pmsProperty.seenLatest,
                    isUsedAlready: existingPropertyNames.has(pmsProperty.name)
                });
            }
        }

        setPmsPropertiesWithUsage(propertiesWithUsage);
    }, [pmsPropertiesData, existingProperties]);

    function handleSortUpdate(column: SortColumn | undefined) {
        if(column !== sortColumn) {
            setSortColumn(column);
            setSortType(nextSortType(undefined));
        }
        else {
            setSortType(nextSortType(sortType));
        }
    }

    function currentSortForColumn(column: SortColumn | undefined): SortType | undefined {
        let type: SortType | undefined = undefined;
        if(!column) {
            type = undefined;
        }
        else if(sortColumn === column) {
            type = sortType;
        }

        return type;
    }

    const pmsPropertyRows = useMemo(() => {
        if(!pmsPropertiesWithUsage || pmsPropertiesWithUsage.length === 0) {
            return null;
        }
        let updated = pmsPropertiesWithUsage;
        if(searchString && searchString.trim().length > 0) {
            updated = updated.filter(item => `${item.name}`.toLowerCase().includes(searchString.toLowerCase()));
        }
        let sortField: SortField | undefined = undefined;
        let sortAscending: boolean | undefined = undefined;
        if(sortColumn && sortType) {
            if(sortColumn === "NAME") {
                sortField = "name";
            }
            else if(sortColumn === "SEEN_EARLIEST") {
                sortField = "seenEarliest";
            }
            else if(sortColumn === "USED_ALREADY") {
                sortField = "isUsedAlready";
            }
            if(sortType === "asc") {
                sortAscending = true;
            }
            else if(sortType === "desc") {
                sortAscending = false;
            }
            if(sortField !== undefined && sortAscending !== undefined) {
                const field = sortField;
                const ascending = sortAscending;
                updated = [...updated].sort((a, b) => {
                    const aValue = a[field];
                    const bValue = b[field];
                    if(aValue < bValue) {
                        return ascending ? -1 : 1;
                    }
                    if(aValue > bValue) {
                        return ascending ? 1 : -1;
                    }
                    return 0;
                });
            }
        }
        else {
            updated = [...updated].sort((a, b) => a.name.localeCompare(b.name));
        }

        if(!updated || updated.length === 0) {
            return null;
        }

        return updated.map((property, index) =>
            <Row key={`${property.name}-${index}`}>
                <Cell>
                    {property.name}
                </Cell>
                <Cell>
                    {property.seenEarliest}
                </Cell>
                <Cell className={css.inUseCell}>
                    {property.isUsedAlready ? <CheckIcon /> : ""}
                </Cell>
                <Cell>
                    <Button isBasic onClick={() => onPropertySelect(property.name)}>Use</Button>
                </Cell>
            </Row>
        );
    }, [pmsPropertiesWithUsage, searchString, sortColumn, sortType, onPropertySelect]);

    if((props.pmsPropertiesData?.length ?? 0) == 0) {
        return <></>;
    }

    return (
        <>
            <Field className={css.inputBox}>
                <MediaInput
                    start={<SearchIcon />}
                    placeholder="Search"
                    value={searchString}
                    onChange={e => setSearchString(e.target.value)}
                />
            </Field>
            {latestDate && (
                <div className={css.dateInfo}>
                    <Label>
                        &nbsp;Property Names known from your PMS (as of {latestDate})
                    </Label>
                </div>
            )}
            <div className={css.hint} ref={bodyRef}>
                <Tooltip
                    content={
                        <>
                            <div>If you don't find a property name in the list:</div>
                            <div style={{marginTop: "0.5rem"}}>
                                <strong>For Yardi:</strong> The property must be included in the respective property list.
                            </div>
                            <div style={{marginTop: "0.5rem"}}>
                                <strong>For Entrata:</strong> Access to the property must be enabled for Vizibly API.
                            </div>
                        </>
                    }
                    popperModifiers={{
                        flip: {
                            boundariesElement: bodyRef.current ?? undefined,
                            behavior: ['left', 'top']
                        },
                    }}
                >
                    <div className={css.hintContent}>
                        <AlertIcon style={{color: "var(--yellow-200)", marginRight: "0.5rem"}} />
                        <span>Can't find a property?</span>
                    </div>
                </Tooltip>
            </div>
            <Table className={css.table}>
                <Head isSticky className={css.tableHead}>
                    <HeaderRow>
                        <SortableCell
                            onClick={() => handleSortUpdate("NAME")}
                            sort={currentSortForColumn("NAME")}
                        >
                            Property Name
                        </SortableCell>
                        <SortableCell
                            onClick={() => handleSortUpdate("SEEN_EARLIEST")}
                            sort={currentSortForColumn("SEEN_EARLIEST")}
                        >
                            First Seen in PMS
                        </SortableCell>
                        <SortableCell
                            onClick={() => handleSortUpdate("USED_ALREADY")}
                            sort={currentSortForColumn("USED_ALREADY")}
                        >
                            Used Already
                        </SortableCell>
                        <HeaderCell></HeaderCell>
                    </HeaderRow>
                </Head>
                <TBody>
                    {pmsPropertyRows || (
                        <Row>
                            <Cell colSpan={4}>No PMS properties found</Cell>
                        </Row>
                    )}
                </TBody>
            </Table>
        </>
    );
}
